import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { 
  ChevronLeft, Camera, Loader2, CheckCircle, AlertCircle,
  Euro, Calendar, FileText, Sparkles, Upload, Image, X
} from 'lucide-react';

const ScanExpensePage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [mounted, setMounted] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
      setScanResult(null);
      setError('');
    }
  };

  const handleScan = async () => {
    if (!image) return;
    
    setScanning(true);
    setError('');

    try {
      // Convert image to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result.split(',')[1];
        
        const token = localStorage.getItem('optitrajet_token');
        const response = await fetch(`${API_URL}/api/optitrajet/expenses/scan-receipt`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ image: base64 })
        });

        const data = await response.json();
        
        if (data.success === false) {
          setError(data.message || 'Analyse non disponible. Saisissez les informations manuellement.');
        } else {
          setScanResult({
            amount: data.amount || '',
            category: data.category || 'other',
            description: data.description || data.vendor || '',
            date: data.date || new Date().toISOString().split('T')[0]
          });
        }
        setScanning(false);
      };
      reader.readAsDataURL(image);
    } catch (err) {
      setError('Erreur lors de l\'analyse. Veuillez réessayer.');
      setScanning(false);
    }
  };

  const handleSave = async () => {
    if (!scanResult) return;
    
    setSaving(true);
    setError('');

    const token = localStorage.getItem('optitrajet_token');
    
    try {
      const response = await fetch(`${API_URL}/api/optitrajet/expenses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: parseFloat(scanResult.amount) || 0,
          category: scanResult.category,
          description: scanResult.description,
          date: scanResult.date
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'enregistrement');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/optitrajet/expenses');
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const clearImage = () => {
    setImage(null);
    setImagePreview(null);
    setScanResult(null);
    setError('');
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#F8FAFC' }}>
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4 animate-in zoom-in duration-300">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-xl font-bold text-slate-800 mb-2">
              Dépense enregistrée !
            </h2>
            <p className="text-slate-500">Redirection en cours...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8" style={{ background: '#F8FAFC', fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center">
          <button 
            onClick={() => navigate('/optitrajet/expenses')}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-slate-600" />
          </button>
          <h1 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-xl font-bold text-slate-800 ml-2">
            Scanner un reçu
          </h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Error */}
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-100 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            <p className="text-amber-700 text-sm">{error}</p>
          </div>
        )}

        {/* Upload Area */}
        {!imagePreview ? (
          <Card className={`border-0 shadow-lg transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <CardContent className="p-8">
              <div 
                className="border-2 border-dashed border-slate-300 rounded-2xl p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-all"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center mx-auto mb-4">
                  <Camera className="h-10 w-10 text-blue-600" />
                </div>
                <h3 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-lg font-semibold text-slate-800 mb-2">
                  Photographier un reçu
                </h3>
                <p className="text-slate-500 text-sm mb-4">
                  L'IA extraira automatiquement les informations
                </p>
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Upload className="h-4 w-4 mr-2" />
                  Choisir une image
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </div>
              
              <div className="mt-6 flex items-center gap-3 text-sm text-slate-500">
                <Sparkles className="h-4 w-4 text-amber-500" />
                <span>Propulsé par l'IA - Extraction automatique du montant, date et catégorie</span>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Image Preview */}
            <Card className={`border-0 shadow-lg mb-6 overflow-hidden transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <CardContent className="p-0 relative">
                <img 
                  src={imagePreview} 
                  alt="Receipt" 
                  className="w-full max-h-64 object-contain bg-slate-100"
                />
                <button
                  onClick={clearImage}
                  className="absolute top-2 right-2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </CardContent>
            </Card>

            {/* Scan Button */}
            {!scanResult && (
              <Button
                onClick={handleScan}
                disabled={scanning}
                className="w-full h-14 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-semibold shadow-lg mb-6"
              >
                {scanning ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Analyse en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5 mr-2" />
                    Analyser avec l'IA
                  </>
                )}
              </Button>
            )}

            {/* Scan Results Form */}
            {scanResult && (
              <Card className="border-0 shadow-lg animate-in slide-in-from-bottom duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4 text-green-600">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">Données extraites</span>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 block">Montant</label>
                      <div className="relative">
                        <Euro className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                        <Input
                          type="number"
                          step="0.01"
                          value={scanResult.amount}
                          onChange={(e) => setScanResult({ ...scanResult, amount: e.target.value })}
                          className="pl-12 h-12 rounded-xl border-slate-200 text-xl font-bold"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 block">Catégorie</label>
                      <select
                        value={scanResult.category}
                        onChange={(e) => setScanResult({ ...scanResult, category: e.target.value })}
                        className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white"
                      >
                        <option value="fuel">Carburant</option>
                        <option value="toll">Péages</option>
                        <option value="parking">Parking</option>
                        <option value="maintenance">Entretien</option>
                        <option value="insurance">Assurance</option>
                        <option value="other">Autre</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 block">Date</label>
                      <Input
                        type="date"
                        value={scanResult.date}
                        onChange={(e) => setScanResult({ ...scanResult, date: e.target.value })}
                        className="h-12 rounded-xl border-slate-200"
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 block">Description</label>
                      <Input
                        value={scanResult.description}
                        onChange={(e) => setScanResult({ ...scanResult, description: e.target.value })}
                        className="h-12 rounded-xl border-slate-200"
                        placeholder="Description"
                      />
                    </div>

                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="w-full h-14 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold shadow-lg mt-4"
                    >
                      {saving ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="h-5 w-5 mr-2" />
                          Enregistrer la dépense
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default ScanExpensePage;
